import Layout from "@/components/Layout";
import { ProductImageThumb } from "@/components/catalog/ProductImageThumb";
import { GenerateGatePassDialog } from "@/components/sales/GenerateGatePassDialog";
import { RecordBalancePaymentDialog } from "@/components/sales/RecordBalancePaymentDialog";
import { RecordInstallmentPaymentDialog, type InstallmentPaymentTarget } from "@/components/sales/RecordInstallmentPaymentDialog";
import { Button } from "@/components/ui/button";
import { buildCategoryProductSchema } from "@/lib/catalog/buildCategorySchema";
import { syncLinksForCategoryFromApi } from "@/lib/catalog/categoryAttributeLinksStore";
import { syncCategoriesFromApi } from "@/lib/catalog/categoriesStore";
import { syncCatalogAttributesFromApi, listCatalogAttributeRecords } from "@/lib/catalog/catalogAttributesStore";
import type { Attribute, AttributeValue } from "@/lib/catalog/types";
import {
  DEAL_TYPE_OPTIONS,
  PROFIT_RULE_OPTIONS,
} from "@/lib/catalog/dealPartners";
import { resolveProductThumbnailUrl } from "@/lib/catalog/productThumbnail";
import { pkrAmountShortLabel } from "@/lib/formatPkrAmount";
import { formatCurrency, getActiveCompanyCurrency } from "@/lib/currencies/currenciesStore";
import { formatDeliveryPayload, nowDeliveryParts, parseDeliveryParts } from "@/lib/sales/gatePass";
import {
  fetchSaleInvoice,
  paySaleBalance,
  paySaleInstallment,
  saleInvoiceErrorMessage,
  updateSaleDelivery,
  type ProfitDistributionSummary,
  type SaleInstallmentRecord,
  type SaleInvoiceRecord,
  type SalePaymentRecord,
  type SalePaymentStatus,
} from "@/lib/sales/saleInvoiceStore";
import { installmentRemaining, installmentStatusClass, installmentStatusLabel } from "@/lib/sales/saleInstallments";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { format, isValid, parseISO } from "date-fns";
import {
  ArrowLeft,
  Download,
  Handshake,
  History,
  Loader2,
  Printer,
  Receipt,
  Store,
  Ticket,
  TrendingUp,
  User,
  Users,
  Wallet,
} from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

function formatAmount(n: number): string {
  return n.toLocaleString("en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function MoneyAmount({
  value,
  className,
  amountClassName,
  emphasis = false,
  showShort = false,
}: {
  value: number | null | undefined;
  className?: string;
  amountClassName?: string;
  emphasis?: boolean;
  showShort?: boolean;
}) {
  if (value == null || !Number.isFinite(Number(value))) {
    return <span className={cn("text-muted-foreground", className)}>—</span>;
  }

  const num = Number(value);
  const short = showShort ? pkrAmountShortLabel(num) : "";

  return (
    <span className={cn("inline-flex flex-col items-end tabular-nums", className)}>
      <span className={cn(emphasis ? "font-semibold text-foreground" : "font-medium text-foreground", amountClassName)}>
        {formatCurrency(num)}
      </span>
      {short ? <span className="text-[10px] text-muted-foreground">{short}</span> : null}
    </span>
  );
}

function paymentMethodLabel(method: string | null | undefined): string {
  if (method === "cash") return "Cash";
  if (method === "online") return "Online";
  if (method === "pay_order") return "Pay Order";
  if (method === "bank") return "Bank";
  if (method === "cheque") return "Cheque";
  return method ?? "—";
}

function tokenTypeLabel(type: string | null | undefined): string {
  if (type === "conditional") return "Conditional";
  if (type === "unconditional") return "Un-Conditional";
  return type ?? "—";
}

function PaymentStatusBadge({ status }: { status: SalePaymentStatus | null | undefined }) {
  if (!status) return null;
  const label = status === "paid" ? "Fully paid" : status === "partial" ? "Partially paid" : "Unpaid";
  return (
    <span
      className={cn(
        "inline-flex rounded-full px-2.5 py-1 text-[11px] font-medium",
        status === "paid"
          ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
          : status === "partial"
            ? "bg-amber-500/15 text-amber-700 dark:text-amber-400"
            : "bg-rose-500/15 text-rose-700 dark:text-rose-400",
      )}
    >
      {label}
    </span>
  );
}

function optionLabel(options: { value: string; label: string }[], value: string | null | undefined): string {
  if (value == null || value === "") return "—";
  return options.find((o) => o.value === value)?.label ?? value;
}

function SummaryRow({
  label,
  value,
  emphasis = false,
  muted = false,
  borderTop = false,
}: {
  label: string;
  value: ReactNode;
  emphasis?: boolean;
  muted?: boolean;
  borderTop?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex min-w-0 items-start justify-between gap-2 py-1.5 text-sm",
        borderTop && "mt-0.5 border-t border-border/40 pt-2",
      )}
    >
      <span
        className={cn(
          "min-w-0 flex-1 break-words leading-snug text-muted-foreground",
          emphasis && "font-medium text-foreground",
        )}
      >
        {label}
      </span>
      <span className={cn("max-w-[58%] shrink-0 text-right leading-snug", muted && "text-muted-foreground")}>
        {value}
      </span>
    </div>
  );
}

function InfoBlock({
  label,
  title,
  subtitle,
  icon: Icon,
}: {
  label: string;
  title: string;
  subtitle?: string | null;
  icon?: typeof User;
}) {
  return (
    <div className="rounded-xl border border-border/40 bg-muted/10 p-3 flex items-start gap-2.5">
      {Icon ? (
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border/50 bg-background text-primary">
          <Icon className="h-4 w-4" />
        </div>
      ) : null}
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
        <p className="mt-0.5 truncate text-xs font-bold text-foreground">{title}</p>
        {subtitle?.trim() ? (
          <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{subtitle}</p>
        ) : null}
      </div>
    </div>
  );
}

function PaymentDetail({
  label,
  value,
  accent = false,
}: {
  label: string;
  value: ReactNode;
  accent?: boolean;
}) {
  return (
    <div
      className={cn(
        "min-w-0 rounded-lg border px-3 py-2.5",
        accent ? "border-primary/20 bg-primary/[0.05]" : "border-border/40 bg-muted/10",
      )}
    >
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="mt-1 min-w-0 break-words text-sm font-medium text-foreground">{value}</div>
    </div>
  );
}

function MetaBadge({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-md border border-border/50 bg-background px-2 py-0.5 text-[11px] font-medium text-foreground">
      {children}
    </span>
  );
}

function formatRatio(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return `${Number(n).toFixed(2)}%`;
}

/** Net-profit rules show profit share (`amount`); owner surplus/commission show payout amounts. */
function usesNetProfitShares(summary: ProfitDistributionSummary | null | undefined): boolean {
  const deal = summary?.deal_type;
  const rule = summary?.profit_rule_type;
  if (deal === "purchased") return true;
  if (deal === "partnership" && (rule === "commission" || rule === "profit_sharing")) return true;
  if (deal === "owner_vehicle" && rule === "profit_sharing") return true;
  return false;
}

function isOwnerSurplusSummary(summary: ProfitDistributionSummary | null | undefined): boolean {
  return summary?.deal_type === "owner_vehicle" && summary?.profit_rule_type === "owner_surplus";
}

function recipientShareAmount(
  row: {
    amount?: number | null;
    final_payable?: number | null;
  },
  summary: ProfitDistributionSummary | null | undefined,
): number | null {
  const profit = row.amount != null ? Number(row.amount) : null;
  const payable = row.final_payable != null ? Number(row.final_payable) : null;

  if (isOwnerSurplusSummary(summary)) {
    if (payable != null && Number.isFinite(payable)) return payable;
  } else if (usesNetProfitShares(summary)) {
    if (profit != null && Number.isFinite(profit)) return profit;
  } else {
    if (profit != null && Number.isFinite(profit) && profit > 0) return profit;
    if (payable != null && Number.isFinite(payable)) return payable;
  }

  if (payable != null && Number.isFinite(payable)) return payable;
  return profit;
}

function RecipientCard({
  name,
  percent,
  amount,
  note,
  emphasis = false,
  payable,
  paid,
  balance,
}: {
  name: string;
  percent?: number | null;
  amount?: number | null;
  note?: string | null;
  emphasis?: boolean;
  payable?: number | null;
  paid?: number | null;
  balance?: number | null;
}) {
  return (
    <div
      className={cn(
        "rounded-lg border px-3 py-2",
        emphasis ? "border-primary/25 bg-primary/[0.05]" : "border-border/40 bg-background/70",
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-foreground">{name}</p>
          {note ? <p className="mt-0.5 text-[11px] text-muted-foreground">{note}</p> : null}
        </div>
        <div className="shrink-0 text-right">
          <p className="text-[11px] font-medium tabular-nums text-muted-foreground">{formatRatio(percent)}</p>
          <MoneyAmount value={amount} emphasis />
        </div>
      </div>

      {payable != null && (payable > 0 || (paid ?? 0) > 0 || (balance ?? 0) > 0) ? (
        <div className="mt-2.5 grid grid-cols-3 gap-2 border-t border-border/25 pt-2 text-[10px] text-muted-foreground leading-normal">
          <div>
            <span className="block text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/80">Payable</span>
            <MoneyAmount value={payable} />
          </div>
          <div>
            <span className="block text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/80">Paid</span>
            <MoneyAmount value={paid} />
          </div>
          <div>
            <span className="block text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/80">Balance</span>
            <MoneyAmount value={balance} emphasis />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SectionCard({
  title,
  icon: Icon,
  children,
  action,
  className,
  bodyClassName,
}: {
  title: string;
  icon?: typeof Wallet;
  children: ReactNode;
  action?: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section className={cn("glass-card h-full overflow-hidden", className)}>
      <div className="flex flex-col gap-2 border-b border-border/30 bg-muted/15 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:px-4 lg:px-5 lg:py-3">
        <div className="flex shrink-0 items-center gap-2">
          {Icon ? <Icon className="h-3.5 w-3.5 shrink-0 text-primary lg:h-4 lg:w-4" /> : null}
          <h2 className="whitespace-nowrap text-sm font-semibold text-foreground lg:text-base">{title}</h2>
        </div>
        {action ? <div className="min-w-0 w-full sm:w-auto sm:max-w-[75%]">{action}</div> : null}
      </div>
      <div className={cn("min-w-0 p-3 sm:p-4 lg:p-5", bodyClassName)}>{children}</div>
    </section>
  );
}

const SaleInvoice = () => {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [sale, setSale] = useState<SaleInvoiceRecord | null>(null);
  const [paymentTarget, setPaymentTarget] = useState<InstallmentPaymentTarget | null>(null);
  const [balanceDialogOpen, setBalanceDialogOpen] = useState(false);
  const [gatePassOpen, setGatePassOpen] = useState(false);
  const [gatePassSaving, setGatePassSaving] = useState(false);
  const [gatePassDate, setGatePassDate] = useState(() => nowDeliveryParts().date);
  const [gatePassTime, setGatePassTime] = useState(() => nowDeliveryParts().time);
  const [checklistValues, setChecklistValues] = useState<Record<string, AttributeValue>>({});
  const [dataLoaded, setDataLoaded] = useState(false);
  const canRecordPayment = can(getStoredAuthUser(), "sale_can_update") || can(getStoredAuthUser(), "sale_can_create");
  const canGenerateGatePass = canRecordPayment;

  const checklistAttributes = useMemo(() => {
    const all = listCatalogAttributeRecords();
    return all.filter((a) => a.isDeliveryChecklist);
  }, [dataLoaded]);

  const reload = () => {
    if (!id) return Promise.resolve();
    return fetchSaleInvoice(id).then((r) => setSale(r));
  };

  useEffect(() => {
    if (!id) {
      setSale(null);
      setLoading(false);
      return;
    }
    let mounted = true;
    setLoading(true);

    // Fetch essential catalog metadata for checklist resolution
    Promise.all([
      syncCategoriesFromApi(),
      syncCatalogAttributesFromApi(),
    ])
      .then(async () => {
        const r = await fetchSaleInvoice(id);
        if (mounted && r) {
          setSale(r);
          if (r.product?.category_id) {
            await syncLinksForCategoryFromApi(r.product.category_id);
          }
          setDataLoaded(true);
        }
      })
      .catch((e) => {
        toast.error(saleInvoiceErrorMessage(e));
        if (mounted) setSale(null);
      })
      .finally(() => mounted && setLoading(false));

    return () => {
      mounted = false;
    };
  }, [id]);

  const invoiceNo = useMemo(() => (sale ? sale.invoice_no || String(sale.id) : ""), [sale?.invoice_no, sale?.id]);

  const installmentOutstanding = useMemo(() => {
    if (sale?.installment_outstanding != null) return sale.installment_outstanding;
    if (!sale?.installments?.length) return null;
    return sale.installments.reduce((sum, row) => sum + installmentRemaining(row), 0);
  }, [sale?.installment_outstanding, sale?.installments]);

  const installmentStats = useMemo(() => {
    const rows = sale?.installments ?? [];
    if (!rows.length) return null;
    const paidCount = rows.filter((r) => r.status === "paid").length;
    return { total: rows.length, paidCount };
  }, [sale?.installments]);

  const distributions = sale?.profit_distributions ?? [];
  const summary = sale?.profit_distribution_summary ?? null;
  const summaryPartners = summary?.partners ?? [];
  const hasSummaryRecipients =
    Boolean(summary?.showroom) ||
    summaryPartners.length > 0 ||
    distributions.length > 0;

  const balanceAmount = sale?.balance_amount ?? sale?.total_amount ?? 0;
  const initialPaymentIsFull =
    Number(sale?.total_amount ?? 0) > 0 &&
    Number(sale?.token_amount ?? 0) >= Number(sale?.total_amount ?? 0) - 0.009;
  const initialPaymentReference =
    sale?.transaction_reference_no || sale?.pay_order_no || null;
  const productImageUrl = useMemo(() => resolveProductThumbnailUrl(sale?.product ?? null), [sale?.product]);

  if (!id) {
    return (
      <Layout title="Invoice">
        <div className="glass-card p-8 text-sm text-muted-foreground">Missing sale id.</div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout title="Invoice">
        <div className="glass-card p-8 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading invoice…
        </div>
      </Layout>
    );
  }

  if (!sale) {
    return (
      <Layout title="Invoice not found">
        <div className="space-y-4">
          <div className="glass-card p-8 text-sm text-muted-foreground">Invoice not found.</div>
          <Link to="/product-sales">
            <Button variant="glass" className="gap-2">
              <ArrowLeft className="h-4 w-4" /> Back to sales
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const customerMeta = [sale.customer?.phone ? `Phone: ${sale.customer.phone}` : null, sale.customer?.city ? `City: ${sale.customer.city}` : null]
    .filter(Boolean)
    .join(" · ");

  const dealTypeLabel = optionLabel(DEAL_TYPE_OPTIONS, sale.product?.deal_type);
  const profitRuleLabel =
    sale.product?.deal_type === "purchased" && !sale.product?.profit_rule_type
      ? "Not applicable"
      : optionLabel(PROFIT_RULE_OPTIONS, sale.product?.profit_rule_type);

  const openGatePassModal = () => {
    const parts = parseDeliveryParts(sale?.delivery_date);
    setGatePassDate(parts.date);
    setGatePassTime(parts.time);

    // Populate checklist attributes initial values
    const initialValues: Record<string, AttributeValue> = {};
    if (sale?.product?.attributes) {
      for (const item of sale.product.attributes) {
        initialValues[item.code] = item.value;
      }
    }
    setChecklistValues(initialValues);

    setGatePassOpen(true);
  };

  const submitGatePass = async () => {
    const payload = formatDeliveryPayload(gatePassDate, gatePassTime);
    if (!payload) {
      toast.error("Delivery date and time are required.");
      return;
    }
    setGatePassSaving(true);
    try {
      const r = await updateSaleDelivery(sale.id, payload, checklistValues);
      if (r.record) {
        setSale(r.record);
      } else {
        await reload();
      }
      toast.success(r.message);
      setGatePassOpen(false);
    } catch (e) {
      toast.error(saleInvoiceErrorMessage(e));
    } finally {
      setGatePassSaving(false);
    }
  };

  const paidTotalAmount = Number(sale.paid_total ?? 0);
  const totalSaleAmount = Number(sale.total_amount ?? 0);
  const percentPaid = totalSaleAmount > 0 ? Math.min(100, Math.max(0, Math.round((paidTotalAmount / totalSaleAmount) * 100))) : 0;

  return (
    <Layout title={`Invoice ${invoiceNo}`}>
      <div className="w-full space-y-4 lg:space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0 space-y-2">
            <Link
              to="/product-sales"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to sales list
            </Link>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 shadow-sm text-primary">
                <Receipt className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2.5">
                  <h1 className="truncate font-display text-2xl font-bold text-foreground sm:text-3xl">
                    Invoice #{invoiceNo}
                  </h1>
                  <PaymentStatusBadge status={sale.payment_status} />
                  <span className="rounded-md border border-border/60 bg-muted/40 px-2 py-0.5 text-[11px] font-semibold text-foreground">
                    {sale.is_installment ? "Installment Sale" : sale.token_amount ? "Token Payment" : "Full Payment"}
                  </span>
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Created on {sale.date && isValid(parseISO(sale.date)) ? format(parseISO(sale.date), "PPP") : (sale.date ?? "—")}
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {canGenerateGatePass && sale.payment_status === "paid" && !sale.delivery_date ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5 h-9 text-xs"
                onClick={openGatePassModal}
              >
                <Ticket className="h-3.5 w-3.5" />
                Generate Gate Pass
              </Button>
            ) : null}
            {sale.payment_status === "paid" && sale.delivery_date ? (
              <>
                <a href={`/sales/${sale.id}/gate-pass/pdf`} target="_blank" rel="noreferrer">
                  <Button type="button" variant="outline" size="sm" className="gap-1.5 h-9 text-xs">
                    <Printer className="h-3.5 w-3.5" />
                    View Gate Pass
                  </Button>
                </a>
                {canGenerateGatePass ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="gap-1.5 h-9 text-xs text-muted-foreground"
                    onClick={openGatePassModal}
                  >
                    <Ticket className="h-3.5 w-3.5" />
                    Update
                  </Button>
                ) : null}
              </>
            ) : null}
            {canRecordPayment && !sale.is_installment && (sale.outstanding ?? sale.balance_outstanding ?? 0) > 0 && (
              <Button
                type="button"
                variant="premium"
                size="sm"
                className="gap-1.5 h-9 text-xs font-semibold px-3.5"
                onClick={() => setBalanceDialogOpen(true)}
              >
                <Wallet className="h-3.5 w-3.5" />
                Record Payment
              </Button>
            )}
            <a href={`/sales/${sale.id}/pdf`} target="_blank" rel="noreferrer">
              <Button variant="premium" size="sm" className="gap-1.5 h-9 text-xs px-3.5">
                <Download className="h-3.5 w-3.5" />
                Download PDF
              </Button>
            </a>
          </div>
        </div>

        {/* Collection Progress Banner */}
        <div className="rounded-xl border border-border/60 bg-gradient-to-r from-background/95 via-muted/25 to-background/95 p-4 shadow-xs backdrop-blur-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-primary/25 bg-primary/10 text-primary">
                <TrendingUp className="h-5 w-5" />
              </div>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                  Collection Progress
                </p>
                <div className="mt-0.5 flex items-baseline gap-2">
                  <span className="text-base font-bold text-foreground">
                    {getActiveCompanyCurrency().symbol} {formatAmount(paidTotalAmount)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    of {getActiveCompanyCurrency().symbol} {formatAmount(totalSaleAmount)}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 sm:text-right">
              <div className="space-y-1">
                <div className="flex items-center justify-between gap-4 text-xs font-semibold">
                  <span className="text-muted-foreground">Payment Status</span>
                  <span className={cn(percentPaid >= 100 ? "text-emerald-600 dark:text-emerald-400 font-bold" : "text-primary")}>
                    {percentPaid}% Paid
                  </span>
                </div>
                <div className="h-2 w-48 sm:w-64 overflow-hidden rounded-full bg-muted">
                  <div
                    className={cn(
                      "h-full transition-all duration-500 rounded-full",
                      percentPaid >= 100
                        ? "bg-emerald-500"
                        : percentPaid > 0
                          ? "bg-gradient-to-r from-primary/70 to-primary"
                          : "bg-muted-foreground/30",
                    )}
                    style={{ width: `${percentPaid}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Overview + Payment — expands across large screens */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          <SectionCard title="Sale Overview" icon={Receipt} className="min-w-0 lg:col-span-5">
            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                <InfoBlock
                  label="Customer"
                  title={sale.customer?.name ?? "—"}
                  subtitle={customerMeta || null}
                  icon={User}
                />
                <InfoBlock
                  label="Salesperson"
                  title={sale.employee?.full_name ?? "—"}
                  subtitle={sale.employee?.employee_code ? `Code: ${sale.employee.employee_code}` : null}
                  icon={User}
                />
              </div>

              {/* Product Hero Thumbnail */}
              <div className="min-w-0 overflow-hidden rounded-xl border border-primary/20 bg-primary/[0.03] p-3">
                <div className="flex items-center gap-3">
                  <div className="h-14 w-20 shrink-0 overflow-hidden rounded-lg border border-border/60 bg-muted shadow-xs">
                    <ProductImageThumb
                      src={productImageUrl}
                      alt={sale.product?.name ?? "Product"}
                      iconClassName="h-6 w-6"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-primary">
                        Selected Vehicle
                      </p>
                      <span className="rounded bg-emerald-500/15 px-1.5 py-0.25 text-[10px] font-semibold text-emerald-700 dark:text-emerald-400">
                        Sold
                      </span>
                    </div>
                    {sale.product?.id ? (
                      <Link
                        to={`/products/${sale.product.id}`}
                        className="mt-0.5 block truncate text-sm font-bold text-foreground hover:text-primary transition-colors"
                      >
                        {sale.product?.name ?? "—"}
                      </Link>
                    ) : (
                      <p className="mt-0.5 truncate text-sm font-bold text-foreground">
                        {sale.product?.name ?? "—"}
                      </p>
                    )}
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-[10px] font-medium text-muted-foreground uppercase">Sale Total</p>
                    <MoneyAmount value={sale.total_amount} emphasis showShort />
                  </div>
                </div>
              </div>

              {sale.description?.trim() ? (
                <div className="rounded-xl border border-border/40 bg-muted/10 p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Notes & Remarks
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-xs text-foreground/90">{sale.description}</p>
                </div>
              ) : null}
            </div>
          </SectionCard>

          <SectionCard
            title="Payment Summary"
            icon={Wallet}
            className="min-w-0 lg:col-span-7"
            action={<PaymentStatusBadge status={sale.payment_status} />}
          >
            <div className="space-y-1">
              <SummaryRow label="Total Sale Price" value={<MoneyAmount value={sale.total_amount} emphasis />} />
              {sale.token_amount != null && Number(sale.token_amount) > 0 ? (
                <>
                  <SummaryRow label="Token Amount" value={<MoneyAmount value={sale.token_amount} />} muted />
                  <SummaryRow label="Token Condition" value={tokenTypeLabel(sale.token_type)} />
                </>
              ) : null}
              <SummaryRow
                label="Balance Amount"
                value={<MoneyAmount value={balanceAmount} emphasis />}
                emphasis
                borderTop
              />
              {sale.is_installment && installmentOutstanding != null ? (
                <>
                  <SummaryRow
                    label="Installments Paid Total"
                    value={<MoneyAmount value={sale.installment_paid_total ?? 0} />}
                  />
                  <SummaryRow
                    label="Installments Outstanding"
                    value={<MoneyAmount value={installmentOutstanding} emphasis />}
                    emphasis
                  />
                </>
              ) : null}
              {!sale.is_installment && sale.balance_paid_total != null && Number(sale.balance_paid_total) > 0 ? (
                <SummaryRow label="Balance Paid Total" value={<MoneyAmount value={sale.balance_paid_total} />} />
              ) : null}
              {!sale.is_installment && sale.balance_outstanding != null ? (
                <SummaryRow
                  label="Remaining Balance Due"
                  value={<MoneyAmount value={sale.balance_outstanding} emphasis />}
                  emphasis
                />
              ) : null}
            </div>
            {canRecordPayment && !sale.is_installment && (sale.balance_outstanding ?? 0) > 0 ? (
              <Button
                type="button"
                variant="premium"
                size="sm"
                className="mt-4 w-full gap-2 h-10 shadow-xs text-xs font-semibold"
                onClick={() => setBalanceDialogOpen(true)}
              >
                <Wallet className="h-4 w-4" />
                Record Balance Payment
              </Button>
            ) : null}
          </SectionCard>

          <SectionCard
            title={initialPaymentIsFull ? "Full Payment Details" : "Token Payment Details"}
            icon={User}
            className="min-w-0 lg:col-span-12"
            action={<PaymentStatusBadge status={sale.payment_status} />}
          >
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              <PaymentDetail
                label={initialPaymentIsFull ? "Sale price" : "Token amount"}
                value={
                  <MoneyAmount
                    value={initialPaymentIsFull ? sale.total_amount : (sale.token_amount ?? 0)}
                    emphasis
                  />
                }
                accent
              />
              <PaymentDetail label="Payment method" value={paymentMethodLabel(sale.payment_method)} />
              <PaymentDetail label="Bank name" value={sale.bank_name || "—"} />
              <PaymentDetail
                label="Reference no."
                value={
                  initialPaymentReference ? (
                    <span className="break-all font-mono text-xs">{initialPaymentReference}</span>
                  ) : (
                    "—"
                  )
                }
              />
              <PaymentDetail
                label="Payment date"
                value={
                  sale.date && isValid(parseISO(sale.date))
                    ? format(parseISO(sale.date), "PP")
                    : (sale.date ?? "—")
                }
              />
              {!initialPaymentIsFull ? (
                <>
                  <PaymentDetail label="Due Date" value={sale.payable_date || "—"} />
                  <PaymentDetail
                    label="Paid amount"
                    value={<MoneyAmount value={sale.paid_total ?? sale.token_amount ?? 0} emphasis />}
                  />
                  <PaymentDetail
                    label="Remaining balance"
                    value={<MoneyAmount value={sale.outstanding ?? sale.balance_outstanding ?? balanceAmount} emphasis />}
                    accent
                  />
                </>
              ) : null}
            </div>
          </SectionCard>
        </div>

        {/* Profit distribution */}
        <SectionCard
          title="Profit Distribution"
          icon={Handshake}
          action={
            <div className="flex flex-wrap justify-start gap-1 sm:justify-end">
              <MetaBadge>Deal · {dealTypeLabel}</MetaBadge>
              <MetaBadge>Rule · {profitRuleLabel}</MetaBadge>
              {(summary?.owner_demand_price ?? sale.product?.owner_demand_price) != null ? (
                <MetaBadge>
                  Demand · Rs.{" "}
                  {formatAmount(Number(summary?.owner_demand_price ?? sale.product?.owner_demand_price))}
                </MetaBadge>
              ) : null}
            </div>
          }
        >
          {!hasSummaryRecipients ? (
            <div className="rounded-lg border border-dashed border-border/60 bg-muted/10 px-3 py-6 text-center">
              <p className="text-sm font-medium text-foreground">No profit distribution recorded</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Updates automatically from deal type, profit rule, sale price, costs, and partners.
              </p>
            </div>
          ) : (
            <div className="space-y-3 lg:space-y-4">
              {isOwnerSurplusSummary(summary) ? (
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:gap-4 xl:grid-cols-3">
                  {summary?.owner_payable ? (
                    <div className="min-w-0 rounded-lg border border-border/40 bg-muted/10 px-3 py-0.5">
                      <p className="py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Owner payable
                      </p>
                      <SummaryRow
                        label="Owner demand price"
                        value={<MoneyAmount value={summary.owner_payable.owner_demand_price} emphasis />}
                      />
                      <SummaryRow
                        label="Total owner payable"
                        value={<MoneyAmount value={summary.owner_payable.total_payable} emphasis />}
                        borderTop
                        emphasis
                      />
                    </div>
                  ) : null}
                  {summary?.surplus_distribution ? (
                    <div className="min-w-0 rounded-lg border border-border/40 bg-muted/10 px-3 py-0.5">
                      <p className="py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Surplus
                      </p>
                      <SummaryRow
                        label="Sale − owner demand"
                        value={<MoneyAmount value={summary.surplus_distribution.surplus} emphasis />}
                      />
                      <SummaryRow
                        label={`Showroom (${formatRatio(summary.surplus_distribution.showroom_percent)})`}
                        value={<MoneyAmount value={summary.surplus_distribution.showroom_amount} />}
                      />
                    </div>
                  ) : null}
                  {summary?.profit_summary ? (
                    <div className="min-w-0 rounded-lg border border-border/40 bg-muted/10 px-3 py-0.5 md:col-span-2 xl:col-span-1">
                      <p className="py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Profit summary
                      </p>
                      <SummaryRow
                        label="Purchase cost"
                        value={<MoneyAmount value={summary.profit_summary.purchase_cost} />}
                        muted
                      />
                      <SummaryRow
                        label="Additional Costs"
                        value={
                          <MoneyAmount
                            value={
                              summary.profit_summary.additional_costs ?? summary.additional_costs ?? 0
                            }
                          />
                        }
                        muted
                      />
                      <SummaryRow
                        label="Owner actual profit"
                        value={<MoneyAmount value={summary.profit_summary.owner_actual_profit} emphasis />}
                      />
                      <SummaryRow
                        label="Showroom profit"
                        value={<MoneyAmount value={summary.profit_summary.showroom_profit} emphasis />}
                      />
                      <SummaryRow
                        label="Total profit"
                        value={<MoneyAmount value={summary.profit_summary.total_profit} emphasis />}
                        borderTop
                      />
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className="grid grid-cols-1 gap-3 lg:grid-cols-3 lg:gap-4">
                <div className="min-w-0 space-y-1.5">
                  <div className="flex items-center gap-1.5 px-0.5">
                    <Store className="h-3.5 w-3.5 shrink-0 text-primary" />
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Showroom
                    </p>
                  </div>
                  {summary?.showroom ? (
                    <RecipientCard
                      name={summary.showroom.name}
                      percent={summary.showroom.percent}
                      amount={recipientShareAmount(summary.showroom, summary)}
                      emphasis
                    />
                  ) : (
                    <p className="rounded-lg border border-dashed border-border/50 px-3 py-2 text-xs text-muted-foreground">
                      No showroom share
                    </p>
                  )}
                </div>

                <div className="min-w-0 space-y-1.5">
                  <div className="flex items-center gap-1.5 px-0.5">
                    <Users className="h-3.5 w-3.5 shrink-0 text-primary" />
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Partners
                      {summaryPartners.length > 0 ? (
                        <span className="ml-1 font-medium normal-case text-foreground/70">
                          ({summaryPartners.length})
                        </span>
                      ) : null}
                    </p>
                  </div>
                  {summaryPartners.length > 0 ? (
                    <div className="space-y-1.5">
                      {summaryPartners.map((row) => {
                        const dbDist = distributions.find(
                          (d) => d.partner_id === row.partner_id
                        );
                        return (
                          <RecipientCard
                            key={row.partner_id ?? row.name}
                            name={row.name}
                            percent={row.profit_ratio ?? row.percent ?? row.purchase_ratio}
                            amount={recipientShareAmount(row, summary)}
                            payable={dbDist?.final_payable ?? row.final_payable}
                            paid={dbDist?.paid_amount ?? row.paid_amount}
                            balance={dbDist?.balance ?? row.balance}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <p className="rounded-lg border border-dashed border-border/50 px-3 py-2 text-xs text-muted-foreground">
                      No deal partners
                    </p>
                  )}
                </div>

                <div className="min-w-0 rounded-lg border border-border/40 bg-muted/10 px-3 py-0.5">
                  <p className="py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {isOwnerSurplusSummary(summary) ? "Payees" : "Totals"}
                  </p>
                  {!isOwnerSurplusSummary(summary) ? (
                    <>
                      <SummaryRow
                        label="Sale price"
                        value={<MoneyAmount value={summary?.sale_price ?? sale.total_amount} emphasis />}
                      />
                      <SummaryRow
                        label="Purchase cost"
                        value={<MoneyAmount value={summary?.purchase_cost ?? sale.product?.purchase_price} />}
                        muted
                      />
                      <SummaryRow
                        label="Additional Costs"
                        value={<MoneyAmount value={summary?.additional_costs ?? 0} />}
                        muted
                      />
                      <SummaryRow
                        label="Net profit"
                        value={<MoneyAmount value={summary?.net_profit} emphasis />}
                        borderTop
                      />
                    </>
                  ) : (
                    <SummaryRow
                      label="Owner actual profit"
                      value={<MoneyAmount value={summary?.profit_summary?.owner_actual_profit} emphasis />}
                    />
                  )}
                  <SummaryRow
                    label="Final distribution"
                    value={
                      <MoneyAmount value={summary?.distribution_total ?? summary?.net_profit} emphasis />
                    }
                    emphasis
                    borderTop
                  />
                </div>
              </div>
            </div>
          )}
        </SectionCard>

        {/* Installments */}
        {sale.installments && sale.installments.length > 0 ? (
          <SectionCard
            title="Installment Schedule"
            icon={Wallet}
            action={
              installmentOutstanding != null && installmentOutstanding > 0 ? (
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-right">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400">
                    Remaining Outstanding
                  </p>
                  <MoneyAmount value={installmentOutstanding} emphasis />
                </div>
              ) : (
                <span className="inline-flex rounded-full bg-emerald-500/15 border border-emerald-500/30 px-3 py-1 text-xs font-bold text-emerald-700 dark:text-emerald-400">
                  Fully Paid
                </span>
              )
            }
          >
            <p className="-mt-1 mb-3 text-xs text-muted-foreground">
              {installmentStats
                ? `${installmentStats.paidCount} of ${installmentStats.total} paid · ${sale.installment_count ?? sale.installments.length} total installments`
                : `${sale.installment_count ?? sale.installments.length} total installments`}
            </p>
            <div className="-mx-4 overflow-x-auto sm:-mx-5 rounded-xl border border-border/40 bg-card">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr className="border-b border-border/40 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-2.5 text-left w-12">#</th>
                    <th className="px-4 py-2.5 text-left">Due date</th>
                    <th className="px-4 py-2.5 text-right">Amount</th>
                    <th className="px-4 py-2.5 text-right">Paid</th>
                    <th className="px-4 py-2.5 text-right">Remaining</th>
                    <th className="px-4 py-2.5 text-center">Status</th>
                    <th className="px-4 py-2.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/20">
                  {sale.installments.map((row: SaleInstallmentRecord) => {
                    const due = parseISO(row.due_date);
                    const remaining = installmentRemaining(row);
                    const canPay = row.status !== "paid";
                    return (
                      <tr key={row.id} className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-2.5 tabular-nums text-xs font-medium text-muted-foreground">{row.installment_no}</td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-xs font-semibold text-foreground">
                          {isValid(due) ? format(due, "PP") : row.due_date}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <MoneyAmount value={row.amount} />
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <MoneyAmount value={row.paid_amount} amountClassName="font-normal text-muted-foreground" />
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <MoneyAmount value={remaining} emphasis={remaining > 0} />
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <span
                            className={cn(
                              "inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold tracking-wide border",
                              row.status === "paid"
                                ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                                : row.status === "overdue"
                                  ? "border-rose-500/30 bg-rose-500/15 text-rose-700 dark:text-rose-400"
                                  : "border-amber-500/30 bg-amber-500/15 text-amber-700 dark:text-amber-400",
                            )}
                          >
                            {installmentStatusLabel(row.status)}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <div className="flex flex-wrap items-center justify-end gap-2">
                            <a
                              href={`/sales/${sale.id}/installments/${row.id}/pdf`}
                              target="_blank"
                              rel="noreferrer"
                              title={`Print installment #${row.installment_no} invoice`}
                            >
                              <Button type="button" variant="outline" size="sm" className="h-7 gap-1 px-2 text-xs">
                                <Printer className="h-3 w-3" />
                                Invoice
                              </Button>
                            </a>
                            {canRecordPayment && canPay ? (
                              <Button
                                type="button"
                                variant="premium"
                                size="sm"
                                className="h-7 text-xs font-semibold px-2.5"
                                onClick={() => setPaymentTarget(row)}
                              >
                                Record Payment
                              </Button>
                            ) : canRecordPayment ? (
                              <span className="px-1 text-xs text-muted-foreground font-medium">
                                {row.paid_at && isValid(parseISO(row.paid_at))
                                  ? format(parseISO(row.paid_at), "PP")
                                  : "Paid"}
                              </span>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </SectionCard>
        ) : null}

        {/* Payment history */}
        {sale.payments && sale.payments.length > 0 ? (
          <SectionCard
            title="Payment History"
            icon={History}
            action={
              <div className="rounded-xl border border-border/40 bg-muted/20 px-3 py-1.5 text-right">
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Total Received</p>
                <MoneyAmount value={sale.paid_total} emphasis />
              </div>
            }
          >
            <div className="-mx-4 overflow-x-auto sm:-mx-5 rounded-xl border border-border/40 bg-card">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr className="border-b border-border/40 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-2.5 text-left">Date</th>
                    <th className="px-4 py-2.5 text-left">Type</th>
                    <th className="px-4 py-2.5 text-left">Method</th>
                    <th className="px-4 py-2.5 text-left">Reference</th>
                    <th className="px-4 py-2.5 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/20">
                  {sale.payments.map((row: SalePaymentRecord) => {
                    const date = row.payment_date ? parseISO(row.payment_date) : null;
                    const reference = [row.bank_name, row.transaction_reference_no, row.pay_order_no, row.notes]
                      .filter((v) => v && String(v).trim())
                      .join(" · ");
                    return (
                      <tr key={row.id} className="hover:bg-muted/20 transition-colors">
                        <td className="whitespace-nowrap px-4 py-2.5 text-xs font-semibold text-foreground">
                          {date && isValid(date) ? format(date, "PP") : (row.payment_date ?? "—")}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className="inline-flex rounded-md border border-border/60 bg-muted/30 px-2 py-0.5 text-[11px] font-semibold text-foreground">
                            {row.transaction_type_label ?? row.transaction_type ?? "Payment"}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-xs font-medium text-muted-foreground">
                          {row.payment_method === "cash" ? "💵 Cash" : row.payment_method === "online" ? "🌐 Online" : row.payment_method === "pay_order" ? "🏦 Pay Order" : (row.payment_method_label ?? paymentMethodLabel(row.payment_method))}
                        </td>
                        <td className="max-w-[260px] truncate px-4 py-2.5 text-xs text-muted-foreground" title={reference || undefined}>
                          {reference || "—"}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <MoneyAmount value={row.amount} emphasis />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </SectionCard>
        ) : null}
      </div>

      <GenerateGatePassDialog
        open={gatePassOpen}
        onOpenChange={setGatePassOpen}
        date={gatePassDate}
        time={gatePassTime}
        onDateChange={setGatePassDate}
        onTimeChange={setGatePassTime}
        saving={gatePassSaving}
        onSubmit={() => void submitGatePass()}
        checklistAttributes={checklistAttributes}
        checklistValues={checklistValues}
        onChecklistValueChange={(code, val) => {
          setChecklistValues((prev) => ({
            ...prev,
            [code]: val,
          }));
        }}
      />

      <RecordBalancePaymentDialog
        open={balanceDialogOpen}
        onOpenChange={setBalanceDialogOpen}
        outstanding={sale.outstanding ?? sale.balance_outstanding ?? 0}
        onSubmit={async (payload) => {
          try {
            const r = await paySaleBalance(sale.id, payload);
            toast.success(r.message);
            if (r.record) {
              setSale(r.record);
            }
            await reload();
          } catch (e) {
            toast.error(saleInvoiceErrorMessage(e));
            throw e;
          }
        }}
      />

      <RecordInstallmentPaymentDialog
        open={paymentTarget != null}
        onOpenChange={(open) => !open && setPaymentTarget(null)}
        installment={paymentTarget}
        saleOutstanding={installmentOutstanding}
        onSubmit={async (payload) => {
          if (!paymentTarget) return;
          try {
            const r = await paySaleInstallment(sale.id, paymentTarget.id, payload);
            toast.success(r.message);
            if (r.record) {
              setSale(r.record);
            }
            await reload();
            setPaymentTarget(null);
          } catch (e) {
            toast.error(saleInvoiceErrorMessage(e));
            throw e;
          }
        }}
      />
    </Layout>
  );
};

export default SaleInvoice;
